import { formatCurrency } from '@/shared/utils';

/**
 * RD Equipamentos integer-unit conversions (demo convention transcribed
 * verbatim — see `docs/superpowers/plans/2026-07-05-rd-equipamentos-backend.md`
 * "Global Constraints"): money in centavos, rates/percents/factors in micro
 * (× 1_000_000; 0,60% → `6000`, factor 1,15 → `1_150_000`).
 */
export const MICRO = 1_000_000;

/** Cents → localized BRL currency string (e.g. `250000` → 'R$ 2.500,00'). */
export function centsToBRL(cents: number): string {
  return formatCurrency(cents / 100);
}

/** Masked currency input → cents (masked input keeps digits = centavos). */
export function brlInputToCents(display: string): number {
  return Number(display.replace(/\D/g, '')) || 0;
}

/**
 * Micro → localized percent display (e.g. `6_000` → '0,60'). `maximumFractionDigits: 2`
 * casa exatamente com o `pct_micro` do demo (`:.2f`); sem ele, `toLocaleString`
 * assumiria `maximumFractionDigits: 3` e divergiria em valores de 3+ casas.
 */
export function microToPercentDisplay(micro: number): string {
  return (micro / 10_000).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Micro → percent display com zeros à direita aparados (SEM o sufixo `%`) —
 * espelha o `_rate_pct` do demo (`modules/pdf_policy.py`), usado apenas no
 * texto de franquia impresso na apólice (`franchise_text`): `100_000` → '10',
 * `15_000` → '1,5', `6_000` → '0,6'. Diferente de `microToPercentDisplay`
 * (sempre 2 casas fixas), que é o correto para o breakdown/taxa efetiva em tela.
 */
export function microToTrimmedPercentDisplay(
  micro: number | null | undefined
): string {
  const value = (micro ?? 0) / 10_000;
  const trimmed = value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return (trimmed || '0').replace('.', ',');
}

/** Localized percent input (e.g. '0,60') → micro (e.g. `6_000`). */
export function percentInputToMicro(display: string): number {
  return (
    Math.round(
      parseFloat(display.replace(/\./g, '').replace(',', '.')) * 10_000
    ) || 0
  );
}

/** Micro → localized factor display (e.g. `1_150_000` → '1,15'). */
export function microToFactorDisplay(micro: number): string {
  return (micro / MICRO).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
  });
}

/** Localized factor input (e.g. '1,15') → micro (e.g. `1_150_000`). */
export function factorInputToMicro(display: string): number {
  return (
    Math.round(
      parseFloat(display.replace(/\./g, '').replace(',', '.')) * MICRO
    ) || 0
  );
}

/**
 * Micro → decimal factor display, trailing zeros trimmed — mirrors the demo
 * `factor_micro` Jinja filter (`modules/money.py`) used for `ratioBucket`
 * (bucket LMI/VR exibido como fator, NÃO como percentual): `300_000` →
 * '0,3', `1_150_000` → '1,15', `0` → '0'. Different from
 * `microToFactorDisplay` above, which always shows 2 fixed decimals (used
 * for form fields, e.g. `discountFactor`).
 */
export function microToTrimmedFactorDisplay(
  micro: number | null | undefined
): string {
  const value = (micro ?? 0) / MICRO;
  const trimmed = value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return (trimmed || '0').replace('.', ',');
}
